import { monthlyPriceFormatting } from "../../../shared-logic/utils/priceUtils";
import { formatPrice } from "../../../../common-deprecated/Globalize";
import {
    type FormatMonthlyPriceType,
    type CommonMonthlyFinanceInfoLabelKey,
    formatMonthlyPrice,
} from "../../../../common-deprecated/priceUtils";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import type { ApiPriceType } from "../../../../common-deprecated/types/ApiModel";
import type { CommonFinanceInfoType, PriceType } from "../../../../common-deprecated/types/CommonTypes";
import type {
    FinanceCarItemType,
    FinanceItemQuoteType,
    FinanceResponseType,
} from "../../../../common-deprecated/features/apheleia/utils/types/Finance";
import { sanitize } from "../../../../common-deprecated/utils/sanitizer";

/**
 * Formats financing monthly prices. Can be formatted as 1 string instead of FormatMonthlyPriceType
 */
export const formatMonthlyFinancePrice = <T extends FormatMonthlyPriceType | string>(
    price: PriceType,
    financeInfo: CommonFinanceInfoType,
    commonSettings: CommonSettingsType,
    priceFormat: string,
    showZeroPrice?: boolean,
    rateWithoutPromotions?: number,
    formatAsString?: boolean,
    secondaryCurrency?: boolean,
): T => {
    if (price.monthly === 0 && !showZeroPrice) return "" as T;
    const term = financeInfo.term || financeInfo.duration || "";
    const tan = financeInfo.annualInterestRate || financeInfo.interestRate || "";

    const financeInfoReplacements: Record<CommonMonthlyFinanceInfoLabelKey, string> = {
        term,
        productName: financeInfo.productName || "",
        taeg: financeInfo.effectiveInterestRate || "",
        tan,
        // residualValue and downPayment are price values, so apply formatPrice as well.
        residualValue: financeInfo.residualValue
            ? formatPrice(financeInfo.residualValue, commonSettings.culture.name, true)
            : "",
        downPayment: financeInfo.downPayment
            ? formatPrice(financeInfo.downPayment, commonSettings.culture.name, true)
            : "",
        totalAmountFinanced: financeInfo.totalAmountFinanced
            ? formatPrice(financeInfo.totalAmountFinanced, commonSettings.culture.name, true)
            : "",
        amountFinanced: financeInfo.amountFinanced
            ? formatPrice(financeInfo.amountFinanced, commonSettings.culture.name, true)
            : "",
        totalCreditWithFees: financeInfo.totalCreditCostWithFee
            ? formatPrice(financeInfo.totalCreditCostWithFee, commonSettings.culture.name, true)
            : "",
        mileage: financeInfo.mileage || "",
        lastInstalment: financeInfo.lastInstalment || "",
        disclaimer: "", // This is supported for USC components but not for NC for now.
        installments: financeInfo.installments || "",
    };

    const monthlyRates = formatMonthlyPrice(
        price.monthly,
        financeInfo,
        commonSettings,
        priceFormat,
        financeInfoReplacements,
        (priceExclPromo: string) => monthlyPriceFormatting(priceExclPromo, priceFormat, financeInfo),
        rateWithoutPromotions,
    );

    // If secondary currency is requested, format the secondary price as well
    if (secondaryCurrency && commonSettings.intl.secondaryCurrencyCode) {
        const secondaryMonthlyRates = formatMonthlyPrice(
            price.monthly,
            financeInfo,
            commonSettings,
            priceFormat,
            financeInfoReplacements,
            (priceExclPromo: string) => monthlyPriceFormatting(priceExclPromo, priceFormat, financeInfo),
            rateWithoutPromotions,
            true,
            true,
        );

        if (formatAsString) {
            const primaryString = monthlyRates.labels.map((label) => label.value).join(" ");

            const secondaryString = secondaryMonthlyRates.labels.map((label) => label.value).join(" ");

            return `${primaryString} / ${secondaryString}` as T;
        } else {
            return {
                ...monthlyRates,
                secondaryLabels: secondaryMonthlyRates.labels,
            } as T;
        }
    }

    return formatAsString
        ? (monthlyRates.labels
              .map((label) => {
                  return label.value;
              })
              .join(" ") as T)
        : (monthlyRates as T);
};

export const formatFinance = (data: FinanceResponseType): Record<string, FinanceCarItemType> => {
    const formattedFinanceData: Record<string, FinanceCarItemType> = {};
    const formatTable = (
        calculations: FinanceItemQuoteType["calculations"] = [],
    ): { name: string; value: string }[] => {
        return calculations.map(({ name, value, format }) => ({
            name,
            value,
            colour: format?.colour,
            bold: format?.b,
            italic: false,
            underline: false,
        }));
    };
    Object.entries(data).forEach(([id, finance]) => {
        if (finance.rate && finance.rate.quote) {
            const disclaimer = {
                value: finance.rate.quote.value ? sanitize(finance.rate.quote.value) : "",
                table: finance.rate.quote.calculations ? formatTable(finance.rate.quote.calculations) : [],
            };
            formattedFinanceData[id] = {
                financeInfo: {
                    annualInterestRate: finance.rate.annualInterestRate
                        ? finance.rate.annualInterestRate.formatted
                        : "",
                    effectiveInterestRate: finance.rate.effectiveInterestRate
                        ? finance.rate.effectiveInterestRate.formatted
                        : "",
                    disclaimer,
                    term: finance.rate.term?.value || finance.rate.duration?.value || "",
                    residualValue: finance.rate.residualValue?.value,
                    downPayment: finance.rate.downpayment?.value,
                    mileage: finance.rate.mileage?.value,
                    duration: finance.rate.duration?.value,
                    interestRate: finance.rate.interestRate?.value,
                    monthlyPayment: finance.rate.monthlyPayment?.value,
                },
                quote: finance.rate.quote,
            };
        }
    });

    return formattedFinanceData;
};

export const mapPrice = (price: ApiPriceType, finance: FinanceCarItemType["financeInfo"]): PriceType => ({
    cash: price.listWithDiscount ?? 0,
    monthly: parseFloat(finance.monthlyPayment ?? "0"),
    monthlyWithoutPromotion: parseFloat(finance.monthlyPayment ?? "0"),
    discount: price.listDiscount ?? 0,
    promotions: [],
    highlight: false,
    exclVat: price.netWithDiscount ?? 0,
    licenseFee: 0,
    onlineCashDiscount: 0,
    onlineMonthlyDiscount: 0,
    onlineCashPromotions: [],
    onlineMonthlyPromotions: [],
});

export const appendMonthlyPrice = (price: PriceType, finance: FinanceCarItemType["financeInfo"]): PriceType => ({
    ...price,
    monthly: parseFloat(finance.monthlyPayment ?? "0"),
    monthlyWithoutPromotion: parseFloat(finance.monthlyPayment ?? "0"),
});
